import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Download,
  Eye,
  Edit,
  Trash2,
  FileText,
  Calendar,
  User,
  Folder,
  Tag,
  Info,
  Loader2,
  Upload,
  History,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';
import { Document, documentsAPI } from '../../lib/api';
import type { DocumentHistoryEntry, DocumentVersionEntry } from '../../lib/api/documents';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getFileIcon } from './utils';
import Changelog, { ChangelogEntry } from '../../components/common/Changelog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/api/types/common';

// CR-5330: Document details popup. Mirrors the structure used by
// PODetailModal / ASNDetailModal in the purchasing module — Details tab
// for metadata + an Audit History tab driven by the shared Changelog
// component. File actions (download / preview / edit / delete) live in
// the footer so they're available from either tab without an extra click.

interface DocumentDetailModalProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
  onDownload: (doc: Document) => void;
  onPreview: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  // CR-5328: parent re-fetches the page after a successful version upload
  // so the new version label / size shows up on the tile/row.
  onAfterVersionUpload?: () => void;
}

export default function DocumentDetailModal({
  document,
  open,
  onClose,
  onDownload,
  onPreview,
  onEdit,
  onDelete,
  onAfterVersionUpload,
}: DocumentDetailModalProps) {
  const { formatDate } = useFormatters();
  const [history, setHistory] = useState<DocumentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // CR-5328: Versions tab state.
  const [versions, setVersions] = useState<DocumentVersionEntry[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [uploadVersionOpen, setUploadVersionOpen] = useState(false);
  // CR-5329: per-viewer acceptance state. Populated from the doc-detail
  // fetch (the list serializer doesn't expose has_accepted), then
  // updated locally after a successful accept POST so the banner flips
  // without a full refetch.
  const [hasAccepted, setHasAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [acceptanceCount, setAcceptanceCount] = useState(0);
  const [accepting, setAccepting] = useState(false);
  // CR-5329 round 2: per-viewer "am I a recipient?" flag from the BE
  // detail serializer. The banner only renders when this is true so
  // customer/uploader-side users don't get the misleading "you must
  // acknowledge" prompt for docs they themselves uploaded.
  const [isRecipient, setIsRecipient] = useState(false);

  const refreshVersions = (docId: number) => {
    setVersionsLoading(true);
    setVersionsError(null);
    documentsAPI.getDocumentVersions(docId)
      .then(res => setVersions(res.data || []))
      .catch(err => {
        console.error('Error loading document versions:', err);
        setVersionsError('Failed to load version history');
      })
      .finally(() => setVersionsLoading(false));
  };

  const refreshHistory = (docId: number) => {
    setHistoryLoading(true);
    setHistoryError(null);
    documentsAPI.getDocumentHistory(docId)
      .then(res => setHistory(res.data || []))
      .catch(err => {
        console.error('Error loading document history:', err);
        setHistoryError('Failed to load audit history');
        toast.error('Failed to load audit history');
      })
      .finally(() => setHistoryLoading(false));
  };

  // CR-5329: fetch the doc detail (which exposes per-viewer has_accepted,
  // accepted_at, acceptance_count) so the banner / badge can render
  // without piggy-backing on the list-serializer payload that's passed
  // in via props.
  const refreshAcceptanceState = (docId: number) => {
    documentsAPI.getDocument(docId)
      .then(res => {
        setHasAccepted(!!res.data.has_accepted);
        setAcceptedAt(res.data.accepted_at || null);
        setAcceptanceCount(res.data.acceptance_count || 0);
        setIsRecipient(!!res.data.is_acceptance_recipient);
      })
      .catch(err => {
        console.error('Error fetching document detail:', err);
      });
  };

  useEffect(() => {
    if (!open || !document) {
      return;
    }
    refreshHistory(document.id);
    refreshVersions(document.id);
    refreshAcceptanceState(document.id);
  }, [open, document]);

  const handleAccept = async () => {
    if (!document) return;
    try {
      setAccepting(true);
      const res = await documentsAPI.acceptDocument(document.id);
      setHasAccepted(!!res.data.has_accepted);
      setAcceptedAt(res.data.accepted_at || null);
      setAcceptanceCount(res.data.acceptance_count || 0);
      // Refresh history so the new "Read & Accepted" event lands.
      refreshHistory(document.id);
      toast.success('Document marked as Read & Accepted');
    } catch (err) {
      console.error('Error accepting document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to record acceptance'));
    } finally {
      setAccepting(false);
    }
  };

  const handleVersionDownload = async (versionId: number) => {
    if (!document) return;
    try {
      const res = await documentsAPI.downloadVersion(document.id, versionId);
      if (res.data.file_url) {
        window.open(res.data.file_url, '_blank');
      } else {
        toast.error('No file attached to this version.');
      }
    } catch (err) {
      console.error('Error downloading version:', err);
      toast.error('Failed to download this version');
    }
  };

  const handleVersionUploaded = () => {
    setUploadVersionOpen(false);
    if (document) {
      refreshVersions(document.id);
      refreshHistory(document.id);
    }
    if (onAfterVersionUpload) onAfterVersionUpload();
    toast.success('New version uploaded');
  };

  if (!document) return null;

  // CR-5376: visibility has THREE states (public/shared/private), not two.
  // The previous `is_public ? 'Public' : 'Private'` collapsed any "shared"
  // doc into "Private", which is what Sarah hit — edit form showed Shared,
  // detail view showed Private. Mirrors the derivation in useDocuments.ts
  // (the source of truth elsewhere in the page).
  const sharedCount = (document.shared_with_company_ids || []).length;
  const visibility = document.is_public
    ? 'Public'
    : sharedCount > 0
      ? 'Shared'
      : 'Private';
  const cleanedDescription = (document.description || '').trim();

  // Synthetic backend ids ('upload-12', 'download-44') are useful in the
  // network tab but Changelog types ChangelogEntry.id as number. Map them
  // to the array index for the React key — the component only uses id as
  // a key.
  const changelogEntries: ChangelogEntry[] = history.map((e, idx) => ({
    id: idx + 1,
    change_date: e.change_date,
    changed_by: e.changed_by,
    changed_by_company: e.changed_by_company || undefined,
    field_changed: e.field_changed,
    old_value: e.old_value,
    new_value: e.new_value,
    event_type: e.event_type,
  }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-start gap-3">
            <div className="text-muted-foreground mt-1 shrink-0">{getFileIcon(document)}</div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg leading-tight break-words">
                {document.title}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                {document.category_name && (
                  <span className="flex items-center gap-1">
                    <Folder className="h-3 w-3" />
                    {document.category_name}
                  </span>
                )}
                {document.formatted_file_size && (
                  <span className="tabular-nums">{document.formatted_file_size}</span>
                )}
                <Badge variant={document.is_public ? 'default' : 'outline'} className="text-[10px] py-0 h-4">
                  {visibility}
                </Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* CR-5329: Read & Accept banner. Only renders when the doc
            requires acceptance AND the current viewer is one of the
            intended recipients (CR-5329 round 2: Sarah saw the banner
            on the customer side because the gate didn't check that
            the viewer was actually a supplier in shared_with_companies
            — uploaders shouldn't be asked to acknowledge their own
            documents). The "Accepted" confirmation still renders
            regardless so an uploader who later accepts (e.g. when a
            doc is shared back) doesn't see a stale banner. */}
        {document.requires_acceptance && (isRecipient || hasAccepted) && (
          hasAccepted ? (
            <div className="px-6 py-2.5 border-b bg-green-50 dark:bg-green-950/30 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
              <span>
                You marked this document as <strong>Read & Accepted</strong>
                {acceptedAt && (
                  <span className="text-muted-foreground"> on {formatDate(acceptedAt)}</span>
                )}
                .
              </span>
              {acceptanceCount > 0 && (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  {acceptanceCount} {acceptanceCount === 1 ? 'acceptance' : 'acceptances'}
                </Badge>
              )}
            </div>
          ) : (
            <div className="px-6 py-3 border-b bg-amber-50 dark:bg-amber-950/30 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">This document requires your acknowledgment.</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Open it, review it, then click Read & Accept below.
                </p>
              </div>
              <Button size="sm" onClick={handleAccept} disabled={accepting}>
                {accepting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                Read & Accept
              </Button>
            </div>
          )
        )}

        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="versions">
                Versions
                {versions.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-[10px] py-0 h-4">
                    {versions.length + 1}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">Audit History</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <div className="space-y-4">
              {cleanedDescription && (
                <div>
                  <DetailLabel icon={Info} text="Description" />
                  <p className="text-sm whitespace-pre-wrap break-words">{cleanedDescription}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailField
                  icon={Folder}
                  label="Folder"
                  value={document.category_name || 'Uncategorized'}
                />
                <DetailField
                  icon={FileText}
                  label="Version"
                  value={document.version || '—'}
                />
                <DetailField
                  icon={User}
                  label="Uploaded by"
                  value={document.uploaded_by_name || '—'}
                />
                <DetailField
                  icon={Calendar}
                  label="Uploaded at"
                  value={document.uploaded_at ? formatDate(document.uploaded_at) : '—'}
                />
                <DetailField
                  icon={Eye}
                  label="Visibility"
                  value={visibility}
                />
                <DetailField
                  icon={FileText}
                  label="File type"
                  value={(document.file_extension || document.document_type || '—').toUpperCase()}
                />
              </div>

              {document.tags && (
                <div>
                  <DetailLabel icon={Tag} text="Tags" />
                  <div className="flex flex-wrap gap-1.5">
                    {document.tags.split(',').map((t, i) => {
                      const tag = t.trim();
                      if (!tag) return null;
                      return (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {document.document_type === 'url' && document.external_url && (
                <div>
                  <DetailLabel icon={Info} text="External URL" />
                  <a
                    href={document.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline break-all"
                  >
                    {document.external_url}
                  </a>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="versions" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Newer versions live on the document; older snapshots are listed below. Uploading a new version preserves the current file as a snapshot.
                </p>
                {onEdit && (
                  <Button size="sm" onClick={() => setUploadVersionOpen(true)}>
                    <Upload className="h-4 w-4 mr-1.5" />
                    New version
                  </Button>
                )}
              </div>

              {/* Current version row — always at the top, marked Current */}
              <div className="border rounded-md p-3 bg-primary/5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="text-[10px] py-0 h-4">Current</Badge>
                      <span className="text-sm font-medium">
                        {document.version || '(no version label)'}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {document.uploaded_by_name || '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {document.uploaded_at ? formatDate(document.uploaded_at) : '—'}
                      </span>
                      {document.formatted_file_size && (
                        <span>{document.formatted_file_size}</span>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => onDownload(document)}>
                    <Download className="h-4 w-4 mr-1.5" />
                    Download
                  </Button>
                </div>
              </div>

              {versionsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading versions...
                </div>
              ) : versionsError ? (
                <div className="text-center py-6 text-muted-foreground">{versionsError}</div>
              ) : versions.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No previous versions.
                </div>
              ) : (
                versions.map(v => (
                  <div key={v.id} className="border rounded-md p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <History className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {v.version_number || '(no version label)'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Replaced by {v.uploaded_by_name || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(v.uploaded_at)}
                          </span>
                          {v.formatted_file_size && <span>{v.formatted_file_size}</span>}
                        </div>
                        {v.notes && (
                          <p className="text-xs text-muted-foreground mt-1.5 italic">
                            "{v.notes}"
                          </p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleVersionDownload(v.id)}>
                        <Download className="h-4 w-4 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading history...
              </div>
            ) : historyError ? (
              <div className="text-center py-8 text-muted-foreground">{historyError}</div>
            ) : (
              <Changelog
                entries={changelogEntries}
                emptyMessage="No activity recorded for this document yet."
              />
            )}
          </TabsContent>
        </Tabs>

        <div className="border-t px-6 py-3 flex items-center justify-end gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onPreview(document)}>
            <Eye className="h-4 w-4 mr-1.5" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(document)}>
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </Button>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(document)}>
              <Edit className="h-4 w-4 mr-1.5" />
              Edit
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => onDelete(document)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      </DialogContent>

      {/* CR-5328: New-version upload sub-dialog */}
      <UploadVersionDialog
        open={uploadVersionOpen}
        document={document}
        onClose={() => setUploadVersionOpen(false)}
        onSuccess={handleVersionUploaded}
      />
    </Dialog>
  );
}

interface UploadVersionDialogProps {
  open: boolean;
  document: Document;
  onClose: () => void;
  onSuccess: () => void;
}

function UploadVersionDialog({ open, document, onClose, onSuccess }: UploadVersionDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setVersion('');
      setNotes('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please choose a file');
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('version', version);
      formData.append('notes', notes);
      await documentsAPI.uploadNewVersion(document.id, formData);
      onSuccess();
    } catch (err) {
      console.error('Error uploading new version:', err);
      toast.error(getApiErrorMessage(err, 'Failed to upload new version'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload new version</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The current file will be saved as a snapshot in the version history. Replacing it now: <strong>{document.title}</strong>.
          </p>
          <div className="space-y-2">
            <Label htmlFor="ver-file">
              File <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ver-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ver-label">Version label</Label>
            <Input
              id="ver-label"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 2.0, v1.1"
            />
            <p className="text-xs text-muted-foreground">
              Will become the document's current version.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ver-notes">Change notes</Label>
            <Textarea
              id="ver-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional — what changed in this version?"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !file}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload version
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DetailLabelProps {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}

function DetailLabel({ icon: Icon, text }: DetailLabelProps) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
      <Icon className="h-3.5 w-3.5" />
      {text}
    </div>
  );
}

interface DetailFieldProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}

function DetailField({ icon, label, value }: DetailFieldProps) {
  return (
    <div>
      <DetailLabel icon={icon} text={label} />
      <p className="text-sm break-words">{value}</p>
    </div>
  );
}
